import clsx from "clsx";
import React from "react";
import { useImmer } from "use-immer";

import { Box, Drawer, Grid, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdminSection } from "@src/components/Navigation/BaseSideNav/AdminSection";
import { BottomNavSection } from "@src/components/Navigation/BaseSideNav/BottomNavSection";
import { StoreManagementSection } from "@src/components/Navigation/BaseSideNav/StoreManagerSection";
import { TopNavSection } from "@src/components/Navigation/BaseSideNav/TopNavSection";
import { OrgPicker } from "@src/components/Navigation/OrgPicker/OrgPicker";
import { useSelector } from "@src/store";

const PREFIX = "SideNavBar";

const classes = {
  logo: `${PREFIX}-logo`,
  logoPadding: `${PREFIX}-logoPadding`,
  drawer: `${PREFIX}-drawer`,
  drawerOpen: `${PREFIX}-drawerOpen`,
  drawerClose: `${PREFIX}-drawerClose`,
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  flexShrink: 0,

  "& .MuiDrawer-paper": {
    boxSizing: "border-box",
    backgroundColor: theme.igBackgroundColors.baseLightHigh,
    border: "none",
    borderRadius: 0,
    overflowX: "hidden",
    borderRight: `1px solid ${theme.igBorders.core.default} !important`,
  },
  [`& .${classes.drawerOpen}`]: {
    width: "250px",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  [`& .${classes.drawerClose}`]: {
    width: "64px",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
}));

interface NavigationDrawerProps {
  isUnauthorized: boolean;
  hideOrgPicker?: boolean;
  customFooter?: React.ReactNode;
  customBody?: React.ReactNode;
}

export const NavigationDrawer = ({
  isUnauthorized,
  customBody,
  customFooter,
  hideOrgPicker,
}: NavigationDrawerProps) => {
  const drawerOpen = useSelector(
    (state) => !state.appSettings.sideNavCollapsed
  );
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useImmer<null | HTMLElement>(null);

  return (
    <StyledDrawer
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: drawerOpen,
        [classes.drawerClose]: !drawerOpen,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: drawerOpen,
          [classes.drawerClose]: !drawerOpen,
        }),
      }}
      sx={{
        overflowX: "hidden",
        width: drawerOpen ? 250 : "64px",
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerOpen ? 250 : "64px",
          boxSizing: "border-box",
          backgroundColor: "#FAFAFA",
          border: "none",
          borderRadius: 0,
          // borderRight: `1px solid ${theme.igBorders.core.default}`,
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <Grid container direction={"column"}>
        <OrgPicker hidePicker={hideOrgPicker} drawerOpen={drawerOpen} />
      </Grid>
      {isUnauthorized ? null : customBody ? (
        <>{customBody}</>
      ) : (
        <>
          <TopNavSection drawerOpen={drawerOpen} />
          <StoreManagementSection drawerOpen={drawerOpen} />
          {customFooter !== undefined ? (
            customFooter
          ) : (
            <BottomNavSection
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              drawerOpen={drawerOpen}
            />
          )}
          <AdminSection drawerOpen={drawerOpen} />
        </>
      )}
    </StyledDrawer>
  );
};
